/**
 * [TD-47.2b + TD-47.3 + TD-47.4] SnakePlayerPool - snake format wrapper
 * around the format-agnostic `PlayerPoolWithDrawer` core. Mirrors
 * `AuctionPlayerPool` from TD-47.2a (founder Gap B.2 → wrapper pattern).
 *
 * Owns:
 *   • Player-list fetch (`/api/players?available_only=false`)
 *   • `useSnakeDraft()` context wiring (submitPick, draftState)
 *   • Single-click commit (Gap 2 verdict B preserved)
 *   • Mounting `SnakeActionDrawer` as the drawer slot
 *   • Snake `isMyTurn` gate from `current_picker_id`
 *
 * v1.2-TD-47.3 deltas (Gaps 2 + 3):
 *   • Passes `<QueueButton player={...}/>` via `renderRowAddon`.
 *   • Reads `selectedPlayerId` from DraftFiltersContext (cross-pane lift).
 *
 * v1.2-TD-47.4 deltas (Drafted Player Visibility):
 *   • Gap 2 verdict C - zero backend change. Flips `available_only: false`
 *     and keeps `limit: 500` for headroom.
 *   • Gap 9 verdict - decorates each player with `is_drafted` derived from
 *     `Object.values(draftState.managers).flatMap(m => m.roster)` (canonical
 *     full source vs the trimmed `pick_history[-30:]` broadcast).
 *   • Gap 8 verdict A - passes `getDraftedLabel(player) → "Picked R{round}.
 *     {pick} by {team_name}"` (snake-format copy).
 *   • Gap 6 verdict A - owns `analysisPlayer` state + mounts
 *     <PlayerAnalysisModal>; PlayerPoolWithDrawer fires `onDraftedRowClick`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { authHeaders, useAuth } from "../../contexts/AuthContext";
import { useSnakeDraft } from "../../contexts/SnakeDraftContext";
import { useDraftFilters } from "../../contexts/DraftFiltersContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useDraftPoolFetch } from "../../hooks/useDraftPoolFetch";
import PlayerPoolWithDrawer from "./PlayerPoolWithDrawer";
import SnakeActionDrawer from "./SnakeActionDrawer";
import QueueButton from "./QueueButton";
import PlayerAnalysisModal from "./PlayerAnalysisModal";
import { diag } from "../../utils/diag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SnakePlayerPool({
  draftState, leagueId, onCanvasUpdate, leagueRoster = null, league = null,
}) {
  const { user } = useAuth();
  const { submitPick } = useSnakeDraft();
  const { selectedPlayerId, setSelectedPlayerId } = useDraftFilters();
  const { isDark } = useTheme();

  const myId = user?.id;
  const isDrafting = draftState?.state === "DRAFTING";
  const isMyTurnToPick = isDrafting && draftState?.current_picker_id === myId;

  // [DIAG] "Nobody could select a player" is indistinguishable from "the UI is
  // fine, it just isn't your turn" without seeing both sides of this compare.
  // Logged once per distinct gate state, not per render.
  const gateSignature = `${isDrafting}|${draftState?.current_picker_id || "none"}|${myId || "none"}`;
  const lastGateRef = useRef(null);
  useEffect(() => {
    if (lastGateRef.current === gateSignature) return;
    lastGateRef.current = gateSignature;
    diag("pool.turn_gate", {
      state: draftState?.state ?? null,
      current_picker_id: draftState?.current_picker_id ?? null,
      my_id: myId ?? null,
      is_my_turn: isMyTurnToPick,
      order_len: (draftState?.draft_order || []).length,
      total_picks_made: draftState?.total_picks_made ?? null,
      timer_remaining: draftState?.timer_remaining ?? null,
    });
  }, [gateSignature]); // eslint-disable-line react-hooks/exhaustive-deps

  // [Item 3] "Draft For LM" - the commissioner can make the pick for whoever
  // is on the clock (a manager with no device phones their pick in). Only
  // while SOMEONE ELSE is on the clock; the server re-checks both the
  // commissioner authority and that the team really is on the clock.
  const onTheClockId = draftState?.current_picker_id || null;
  const canProxyPick =
    isDrafting &&
    !!onTheClockId &&
    !isMyTurnToPick &&
    !!league?.commissioner_id &&
    league.commissioner_id === myId;
  const proxyTeamName = canProxyPick
    ? (draftState?.managers?.[onTheClockId]?.team_name || "the clock")
    : null;

  const [players, setPlayers] = useState([]);
  // [Egress fix 2026-07-31] Once-per-mount fetch with retry + visible
  // error state. See useDraftPoolFetch comment for the full rationale.
  const {
    players: fetchedPlayers,
    loadError,
    loading: poolLoading,
    retry: retryPoolFetch,
  } = useDraftPoolFetch(leagueId);
  useEffect(() => { setPlayers(fetchedPlayers); }, [fetchedPlayers]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // [TD-47.4 Gap 6] Modal state owned at the wrapper.
  const [analysisPlayer, setAnalysisPlayer] = useState(null);
  // [Sprint §Part 1] Hydrate ADP + PGF rank + composite from edge APIs
  // (union of sleepers/overvalued/watchlist). See AuctionPlayerPool for
  // rationale - same enrichment pattern.
  const [pgfEnrichment, setPgfEnrichment] = useState({});
  useEffect(() => {
    if (!leagueId) return;
    let alive = true;
    const sf = draftState?.scoring_format || "half_ppr";
    const paramsBase = new URLSearchParams({
      league_id: leagueId, season_year: "2025", scoring_format: sf,
    });
    const fetchOne = (path, extra = "") =>
      axios.get(
        `${API}/analytics/edge/${path}?${paramsBase.toString()}${extra}`,
        { headers: authHeaders() },
      ).then((r) => r.data).catch(() => null);
    (async () => {
      const [finds, overvalued, watchlist] = await Promise.all([
        fetchOne("finds", "&limit=500"),
        fetchOne("overvalued", "&limit=500"),
        fetchOne("watchlist", "&min_edge_pct=0.0"),
      ]);
      if (!alive) return;
      const merge = {};
      const eat = (rows) => {
        (rows || []).forEach((r) => {
          merge[r.player_id] = {
            adp_rank_position: r.adp_rank_position ?? merge[r.player_id]?.adp_rank_position,
            pgf_rank_position: r.pgf_rank_position ?? merge[r.player_id]?.pgf_rank_position,
            insufficient_data: r.badge === "INSUFFICIENT DATA",
          };
        });
      };
      eat(finds?.finds);
      eat(overvalued?.overvalued);
      eat(watchlist?.watchlist);
      setPgfEnrichment(merge);
    })();
    return () => { alive = false; };
  }, [leagueId, draftState?.scoring_format]);

  // [Egress fix 2026-07-31] The pool fetch used to run keyed on
  // `draftState?.total_picks_made`, forcing a full /api/players refetch
  // on every pick per connected client. Availability derives from
  // draftState.managers via draftedById below and DraftContext pushes
  // full state on every pick over the websocket. Fetch moved into
  // useDraftPoolFetch (leagueId only) with bounded retry + visible
  // error state so a failed initial load does not silently strand a
  // manager for the whole draft.

  // [TD-47.4 Gap 9] Build draftedById from managers' rosters (canonical full
  // source). Snake roster rows carry round + pick_in_round at submit time.
  const draftedById = useMemo(() => {
    const map = {};
    const managers = draftState?.managers || {};
    Object.entries(managers).forEach(([teamId, mgr]) => {
      (mgr?.roster || []).forEach((row) => {
        const pid = row?.player?.id;
        if (!pid) return;
        map[pid] = {
          team_id: teamId,
          team_name: mgr?.team_name || "Team",
          round: row?.round,
          pick_in_round: row?.pick_in_round,
        };
      });
    });
    return map;
  }, [draftState?.managers, draftState?.total_picks_made]);

  const decoratedPlayers = useMemo(
    () =>
      players.map((p) => {
        const enrich = pgfEnrichment[p.id];
        const withDrafted = draftedById[p.id] ? { ...p, is_drafted: true } : p;
        return enrich ? { ...withDrafted, ...enrich } : withDrafted;
      }),
    [players, draftedById, pgfEnrichment],
  );

  // [TD-47.4 Gap 8] Snake-format drafted-row label.
  const getDraftedLabel = (player) => {
    const ev = draftedById[player.id];
    if (!ev) return null;
    const r = ev.round ?? "?";
    const p = ev.pick_in_round ?? "?";
    return `Picked R${r}.${p} by ${ev.team_name}`;
  };

  // Resolve selected from the player list using the lifted context id.
  const selected =
    (selectedPlayerId && decoratedPlayers.find((p) => p.id === selectedPlayerId)) ||
    null;
  const setSelected = (player) => setSelectedPlayerId?.(player?.id || null);

  // [Gap 2 verdict B] Single-click commit. No confirmation step.
  const handleDraft = async () => {
    if (!selected || busy) return;
    setBusy(true);
    setError(null);
    const res = await submitPick(selected.id, canProxyPick ? onTheClockId : null);
    if (res?.ok) {
      setSelected(null);
    } else {
      setError(res?.error || "Pick rejected");
    }
    setBusy(false);
  };

  const handleCancel = () => {
    setSelected(null);
    setError(null);
  };

  // Reset error/busy whenever the user picks a different player.
  useEffect(() => {
    setError(null);
  }, [selectedPlayerId]);

  const onClockBanner =
    canProxyPick && !selected ? (
      <div
        className="px-3 py-2 border-b border-[#FF6B00]/30 shrink-0 text-center"
        style={{ background: "rgba(255,107,0,0.06)" }}
        data-testid="snake-pool-proxy-banner"
      >
        <p className="font-display font-black text-xs uppercase tracking-wider text-[#FF6B00]">
          {proxyTeamName} is on the clock - tap a player to draft for them
        </p>
      </div>
    ) : isMyTurnToPick && !selected ? (
      <div
        className="px-3 py-2 border-b border-[#FF6B00]/30 shrink-0 text-center animate-orange-pulse"
        style={{ background: "rgba(255,107,0,0.08)" }}
        data-testid="snake-pool-on-clock-banner"
      >
        <p className="font-display font-black text-xs uppercase tracking-wider text-[#FF6B00]">
          YOU'RE ON THE CLOCK - tap a player to draft
        </p>
      </div>
    ) : null;

  return (
    <>
      {loadError && (
        <div
          className="mb-3 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-400 text-sm flex items-center justify-between"
          data-testid="snake-pool-load-error"
        >
          <span>Player pool failed to load. {loadError}</span>
          <button
            type="button"
            onClick={retryPoolFetch}
            className="px-3 py-1 rounded bg-red-500/20 hover:bg-red-500/30 text-xs font-display font-bold uppercase tracking-wide"
            data-testid="snake-pool-retry"
          >Retry</button>
        </div>
      )}
      <PlayerPoolWithDrawer
        players={decoratedPlayers}
        isMyTurn={isMyTurnToPick || canProxyPick}
        onClockBanner={onClockBanner}
        selected={selected}
        setSelected={setSelected}
        testIdPrefix="snake-pool"
        leagueRoster={leagueRoster}
        renderRowAddon={(player) => <QueueButton player={player} />}
        onDraftedRowClick={setAnalysisPlayer}
        onCanvasUpdate={onCanvasUpdate}
        getDraftedLabel={getDraftedLabel}
        renderDrawer={(player) => (
          <SnakeActionDrawer
            player={player}
            onDraft={handleDraft}
            onCancel={handleCancel}
            busy={busy}
            error={error}
            proxyTeamName={proxyTeamName}
          />
        )}
      />
      {analysisPlayer && (
        <PlayerAnalysisModal
          leagueId={leagueId}
          player={analysisPlayer}
          onClose={() => setAnalysisPlayer(null)}
          isDark={isDark}
          draftEpoch={draftState?.total_picks_made || 0}
          showPgfValue={false}
        />
      )}
    </>
  );
}
